import { doc, getDoc, setDoc } from 'firebase/firestore';
import Failure from '../../../core/Failure';
import { FirestoreCollections, firestore } from '../../../firebase.config';
import { Authentication } from '@leedpulse-monorepo/classes';
import { ICreditMap, IPlanPrice } from '@leedpulse-monorepo/interfaces';
import { UserRole, EProductType } from '@leedpulse-monorepo/enums';
import { TPlanFeatures } from '@leedpulse-monorepo/types';
import { IPlanLink } from '@leedpulse-monorepo/interfaces';

async function createPlan(
  payload: CreatePlanPayload,
  auth: Authentication
): Promise<void> {
  if (![UserRole.owner, UserRole.admin].includes(auth.role)) {
    throw new CreatePlanFailure('Unauthorized', 'unauthorized');
  }

  if (payload.id === '' ||
    payload.color === '' ||
    payload.credits === null ||
    payload.features === null ||
    payload.name === '' ||
    payload.price === null ||
    payload.product === null ||
    payload.recurrence === null ||
    payload.recurrenceCreditsReset === null ||
    payload.links === null ||
    payload.downgradableTo === null ||
    payload.upgradableTo === null) {
      throw new CreatePlanFailure('Les informations sont incomplètes');
  }

  try {
    const planDocData = {
      id: payload.id,
      color: payload.color,
      credits: payload.credits,
      features: payload.features,
      name: payload.name,
      price: payload.price,
      product: payload.product,
      recurrence: payload.recurrence,
      recurrence_credits_reset: payload.recurrenceCreditsReset,
      links: payload.links,
      active: false, // False by default to prevent uncomplete plan to be used by customers
      archived: false,
      downgradable_to: payload.downgradableTo,
      upgradable_to: payload.upgradableTo,
      payg: payload.payg,
    };

    const db = firestore;

    // First we check if the plan already exists
    // If it does, we throw an error
    const planDocRef = doc(db, FirestoreCollections.plans, planDocData.id);
    const planDocSnap = await getDoc(planDocRef);

    if (planDocSnap.exists()) {
      throw new CreatePlanFailure('Plan already exists', 'plan-exists');
    }

    // If it doesn't, we create the plan
    await setDoc(planDocRef, planDocData);

    return;
  } catch (e) {
    console.log('error: ', e);
    throw new CreatePlanFailure();
  }
}

export class CreatePlanPayload {
  id: string;
  color: string;
  credits: ICreditMap;
  features: TPlanFeatures;
  name: string;
  price: IPlanPrice;
  product: EProductType;
  recurrence: boolean;
  recurrenceCreditsReset: boolean;
  links: IPlanLink[];
  downgradableTo: string[];
  upgradableTo: string[];
  payg: ICreditMap | undefined;
  constructor(
    id: string,
    color: string,
    credits: ICreditMap,
    features: TPlanFeatures,
    name: string,
    price: IPlanPrice,
    product: EProductType,
    recurrence: boolean,
    recurrenceCreditsReset: boolean,
    links: IPlanLink[],
    downgradableTo: string[],
    upgradableTo: string[],
    payg: ICreditMap,
  ) {
    this.id = id;
    this.color = color;
    this.credits = credits;
    this.features = features;
    this.name = name;
    this.price = price;
    this.product = product;
    this.recurrence = recurrence;
    this.recurrenceCreditsReset = recurrenceCreditsReset;
    this.links = links;
    this.downgradableTo = downgradableTo;
    this.upgradableTo = upgradableTo;
    this.payg = payg;
  }
}

export class CreatePlanFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}

export default createPlan;
